<template>
  <div class="locations-slide">
    <!-- Header -->
    <div class="header locations-slide__header">
      <h1 class="global-h1 header__h1">Locations &amp; Adventures</h1>
      <div class="header__subtitle">
        <p style="margin: 0 0 10px 0; color: rgba(91, 91, 91, 1); font: 14px/18px sans-serif">Regardless of your choice below, you will receive 80+ pieces of content.</p>
        <p style="margin-bottom: 0; color: rgba(91, 91, 91, 1); font: 14px/18px sans-serif">
          The sliders below help direct the creator to highlight more locations,<br />
          or to spend more time at fewer locations.
        </p>
      </div>
    </div>
    <!-- / Header -->

    <div class="locations-slide__divider"></div>

    <h4 class="global-h4 locations-slide__h4">How many locations would you like highlighted?</h4>
    <SrpSlider
      class="locations-slide__slider"
      :min="MinLocationCount"
      :max="MaxLocationCount"
      v-model:value="_requestedLocationCount"
      @update:value="newValue => emit('update:requestedLocationCount', newValue)"
    />

    <div class="locations-slide__divider"></div>

    <h4 class="global-h4 locations-slide__h4">How many adventures would you like created?</h4>
    <SrpSlider
      class="locations-slide__slider"
      :min="MinAdventureCount"
      :max="MaxAdventureCount"
      v-model:value="_requestedAdventureCount"
      @update:value="newValue => emit('update:requestedAdventureCount', newValue)"
    />

    <NoteWithIcon v-if="isWarningNoteVisible" class="locations-slide__note" color="red" size="tiny">
      <template #icon><IconEmbedded name="warning_2-5" :size="18" /></template>
      <template #text>
        <span>
          For more adventures, increase the number of locations<br />
          being highlighted. (so the creator can build full itineraries)
        </span>
      </template>
    </NoteWithIcon>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SrpSlider from "@components/ui/SrpSlider.vue";

// Constants
import { RequestedLocationCountDefault, MinLocationCount, MaxLocationCount, RequestedAdventuresCountDefault, MinAdventureCount, MaxAdventureCount } from "@/constants/collab";

const props = withDefaults(
  defineProps<{
    requestedLocationCount: number | null;
    requestedAdventureCount: number | null;
  }>(),
  {
    requestedLocationCount: null,
    requestedAdventureCount: null,
  }
);

const emit = defineEmits<{
  (e: "onMounted"): void;
  (e: "update:requestedLocationCount", value: number): void;
  (e: "update:requestedAdventureCount", value: number): void;
}>();

// Init values ================================================================
const _requestedLocationCount = ref<number>(RequestedLocationCountDefault);
const _requestedAdventureCount = ref<number>(RequestedAdventuresCountDefault);

onMounted(() => {
  _requestedLocationCount.value = props.requestedLocationCount || RequestedLocationCountDefault;
  _requestedAdventureCount.value = props.requestedAdventureCount || RequestedAdventuresCountDefault;

  if (props.requestedLocationCount !== _requestedLocationCount.value) {
    emit("update:requestedLocationCount", _requestedLocationCount.value);
  }
  if (props.requestedAdventureCount !== _requestedAdventureCount.value) {
    emit("update:requestedAdventureCount", _requestedAdventureCount.value);
  }
});

// Is warning note visible ====================================================
const isWarningNoteVisible = computed<boolean>(() => {
  if (_requestedLocationCount.value <= 4) return _requestedAdventureCount.value > 2;
  else if (_requestedLocationCount.value <= 7) return _requestedAdventureCount.value > 3;
  else if (_requestedLocationCount.value >= 8) return _requestedAdventureCount.value > 4;

  return false;
});

const isFormValid = computed<boolean>(() => {
  return !isWarningNoteVisible.value;
});

// Inform the parent component that this component is shown ===================
onMounted(() => emit("onMounted"));

// Define expose ==============================================================
defineExpose({
  isFormValid,
});
</script>

<style scoped lang="scss">
// Header =====================================================================
.header {
  &__h1 {
    margin-bottom: 8px;
  }

  &__subtitle {
    color: rgba(91, 91, 91, 1);
    font-family: sans-serif;
  }
}

// Locations slide ============================================================
.locations-slide {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &__header {
    margin-bottom: 20px;
  }

  &__h4 {
    margin-bottom: 5px;
  }

  &__divider {
    width: 100%;
    height: 0;
    margin-bottom: 20px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  }

  &__slider {
    margin-bottom: 23px;
  }

  &__note {
  }
}
</style>
